import React, { useState, useEffect, useRef } from "react";
import "../index.css";
import "../App.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bar } from "react-chartjs-2";
import RadarChart from "./RadarChart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";

const MEDALS = ["🥇", "🥈", "🥉"];

const GameSummary = ({ gameId }) => {
  const { t, i18n } = useTranslation();
  const [language, setLanguage] = useState("fr");
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfExporting, setPdfExporting] = useState(false);
  const [barImgUrl, setBarImgUrl] = useState(null);
  const [radarImgUrl, setRadarImgUrl] = useState(null);
  const [gameStatus, setGameStatus] = useState(null);
  const [decisionMetrics, setDecisionMetrics] = useState(null);
  const navigate = useNavigate();
  const pdfRef = useRef(null);
  const hiddenChartsRef = useRef(null);

  useEffect(() => {
    if (language) i18n.changeLanguage(language);
  }, [language, i18n]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [summary, status, metrics] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/game/${gameId}/get_game_summary`).then((r) => r.json()),
        fetch(`${process.env.REACT_APP_API_URL}/game/${gameId}/get_current_status`).then((r) => r.json()),
        fetch(`${process.env.REACT_APP_API_URL}/game/${gameId}/get_round_metrics`).then((r) => r.json()),
      ]);
      setGameData(summary);
      setGameStatus(status);
      setDecisionMetrics(metrics);
      if (summary.language) setLanguage(summary.language);
      setLoading(false);
    };
    load();
  }, [gameId]);

  const handleDownloadPDF = async () => {
    setPdfLoading(true);

    // Capture chart canvases from the hidden off-screen container
    let barUrl = null;
    let radarUrl = null;
    if (hiddenChartsRef.current) {
      const canvases = hiddenChartsRef.current.querySelectorAll("canvas");
      if (canvases[0]) barUrl = canvases[0].toDataURL("image/png");
      if (canvases[1]) radarUrl = canvases[1].toDataURL("image/png");
    }

    setBarImgUrl(barUrl);
    setRadarImgUrl(radarUrl);
    setPdfExporting(true);

    // Wait for React to render the img elements into the PDF content
    await new Promise((r) => setTimeout(r, 300));

    const html2pdf = (await import("html2pdf.js")).default;
    await html2pdf()
      .set({
        margin: [8, 8, 8, 8],
        filename: `rapport_${gameId}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false, scrollY: 0 },
        jsPDF: { unit: "mm", format: "a4", orientation: "landscape" },
        pagebreak: { mode: "avoid-all" },
      })
      .from(pdfRef.current)
      .save();

    setPdfExporting(false);
    setBarImgUrl(null);
    setRadarImgUrl(null);
    setPdfLoading(false);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <img src="/loader.gif" className="loader" alt="Chargement..." />
      </div>
    );
  }

  if (!gameData || gameData.length === 0) {
    return <div>Aucune donnée de jeu disponible</div>;
  }

  const { best_players: podium, worst_player: effortPlayer, stat_game: stats, number_of_decisions: numberOfDecisions } = gameData;

  const listSectors = ["agriculture", "industry", "public", "consumer"];
  const finishGraph = gameStatus?.finish_graph;
  const showAdditionalScore = gameStatus?.show_additional_score;

  const stackedSectors = [
    { label: t("Agriculture"),   color: "#b7e0e1", key: "agriculture" },
    { label: t("Industrie"),     color: "#205673", key: "industry" },
    { label: t("Public"),        color: "#db6c52", key: "public" },
    { label: t("Consommateurs"), color: "#f5c842", key: "consumer" },
  ];

  const barChartData = finishGraph ? {
    labels: finishGraph.map((item) => item.tour + 1),
    datasets: [
      ...stackedSectors.map((sector, i) => {
        const data = finishGraph.map((item) => item[sector.key].total);
        return {
          label: sector.label,
          backgroundColor: sector.color,
          borderSkipped: false,
          borderRadius: data.map((v, colIdx) => {
            if (v === 0) return 0;
            const isTop = stackedSectors.slice(i + 1).every(
              (later) => (finishGraph[colIdx][later.key].total || 0) === 0
            );
            return isTop ? { topLeft: 6, topRight: 6 } : 0;
          }),
          data,
          stack: "s",
        };
      }),
      {
        label: t("Courbe d'objectif"),
        data: (() => {
          const nbRounds = gameStatus?.nb_rounds ?? 5;
          const target = 20 * nbRounds;
          const totalYears = nbRounds + 5;
          const R = finishGraph.length;
          return Array.from({ length: totalYears }, (_, i) => {
            const x = i + 1;
            const workshopDone = Math.min(R, nbRounds);
            if (x < nbRounds) {
              return { x, y: (target * x * Math.min(workshopDone, x)) / (nbRounds * nbRounds) };
            } else {
              const tailDivisors = [nbRounds + 1, nbRounds + 0.5, nbRounds + 0.3, nbRounds + 0.2, nbRounds + 0.1, nbRounds];
              const divisor = tailDivisors[x - nbRounds] ?? nbRounds;
              return { x, y: Math.round((target * workshopDone) / divisor) };
            }
          });
        })(),
        type: "line",
        fill: false,
        borderColor: "#e74c3c",
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 4,
        pointBackgroundColor: "#e74c3c",
        tension: 0.3,
      },
    ],
  } : null;

  const barChartOptions = {
    animation: false,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      datalabels: false,
      legend: { position: "bottom", labels: { padding: 16, usePointStyle: true, font: { size: 11 }, color: "#205673" } },
      tooltip: { backgroundColor: "rgba(32,86,115,0.92)", cornerRadius: 8, padding: 10 },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: "#205673" } },
      y: { stacked: true, beginAtZero: true, grid: { color: "rgba(32,86,115,0.07)" }, ticks: { color: "#94a3b8" } },
    },
  };

  return (
    <>
      {/* ── Charts rendus hors écran pour capture canvas ── */}
      <div
        ref={hiddenChartsRef}
        style={{ position: "fixed", left: -9999, top: 0, width: 900, pointerEvents: "none", visibility: "hidden" }}
      >
        {barChartData && (
          <div style={{ position: "relative", height: 380 }}>
            <Bar data={barChartData} options={barChartOptions} />
          </div>
        )}
        {showAdditionalScore && decisionMetrics && (
          <div style={{ position: "relative", height: 400 }}>
            <RadarChart data_radar={decisionMetrics} t={t} />
          </div>
        )}
      </div>

      <div className="gs-page">
        <div ref={pdfRef}>
          {/* ── Hero ── */}
          <div className="gs-hero">
            <span className="gs-hero-emoji">🏆</span>
            <h1 className="gs-hero-title">{t("game-summary-title")}</h1>
          </div>

          {/* ── Score (PDF uniquement) ── */}
          {pdfExporting && gameStatus && (() => {
            const nbRounds = gameStatus.nb_rounds ?? 5;
            const score = Math.round((100 - gameStatus.forecasting_of_water_budget) / (20 * nbRounds) * 100);
            const scoreClass = score >= 100 ? "gg-score-hero--success" : score >= 90 ? "gg-score-hero--warning" : "gg-score-hero--fail";
            return (
              <div className={`gg-score-hero ${scoreClass}`} style={{ margin: "20px 0" }}>
                <div className="gg-score-circle">
                  <span className="gg-score-number">{score}</span>
                  <span className="gg-score-unit">%</span>
                </div>
                <div className="gg-score-label">
                  <span className="gg-score-title">{score >= 100 ? t("congratulations") : t("endOfLastRound")}</span>
                  <span className="gg-score-sub">{t("waterSavingsRecap")}</span>
                </div>
              </div>
            );
          })()}

          {/* ── Graphiques intertour — uniquement dans le PDF via img ── */}
          {pdfExporting && (barImgUrl || radarImgUrl) && (
            <>
            <div style={{ pageBreakBefore: "always" }} />
            {barImgUrl && (
              <section className="gs-section" style={{ pageBreakInside: "avoid" }}>
                <h2 className="gs-section-title">{t("waterSavingsEvolution")}</h2>
                <div className="gs-chart-box">
                  <img src={barImgUrl} alt="histogramme" style={{ width: "100%" }} />
                </div>
              </section>
            )}
            {radarImgUrl && (
              <section className="gs-section" style={{ pageBreakInside: "avoid", marginTop: 32 }}>
                <h2 className="gs-section-title">{t("impactOtherIndicators")}</h2>
                <div className="gs-chart-box">
                  <img src={radarImgUrl} alt="radar" style={{ width: "100%" }} />
                </div>
              </section>
            )}
            </>
          )}

          {/* ── Acteurs mis en avant ── */}
          <div style={{ pageBreakBefore: "always" }} />
          <section className="gs-section">
            <h2 className="gs-section-title">{t("game-summary-actors-highlighted")}</h2>
            <div className="gs-cards-row">
              <div className="gs-card gs-card--podium">
                <ol className="gs-podium-list">
                  {podium.map((player, i) => (
                    <li key={i} className={`gs-podium-item gs-podium-item--${i + 1}`}>
                      <span className="gs-podium-medal">{MEDALS[i] || `${i + 1}.`}</span>
                      <span className="gs-podium-name">{player}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="gs-card gs-card--effort">
                <span className="gs-card-emoji">💪</span>
                <p className="gs-card-label">{t("game-summary-most-effort")}</p>
                <p className="gs-card-value">{effortPlayer}</p>
              </div>
            </div>
          </section>

          {/* ── Résultats équipe ── */}
          <section className="gs-section">
            <h2 className="gs-section-title">{t("game-summary-team-results")}</h2>
            <div className="gs-cards-row">
              <div className="gs-card gs-card--stat">
                <div className="gs-stat-number">{numberOfDecisions}</div>
                <div className="gs-stat-label">{t("game-summary-decisions-taken")}</div>
              </div>
              <div className="gs-card gs-card--stat">
                <div className="gs-stat-number">{stats}%</div>
                <div className="gs-stat-label">{t("game-summary-better-than-other-games")}</div>
              </div>
            </div>
          </section>
        </div>

        {/* ── Actions ── */}
        <div className="gs-actions">
          <button
            className="gs-download-btn"
            onClick={handleDownloadPDF}
            disabled={pdfLoading}
          >
            <FontAwesomeIcon icon={faDownload} />
            {pdfLoading ? "Génération..." : t("downloadPDF")}
          </button>
          <button className="gs-home-btn" onClick={() => navigate("/")}>
            {t("game-summary-return-home")}
          </button>
        </div>
      </div>
    </>
  );
};

export default GameSummary;
